const { pathToRegexp, match, compile } = require("path-to-regexp");

const debugFactory = require("../../debug");

const { calcGatewayPathname } = require("./calcGatewayPathname");
const InvalidProxyRouteError = require("./InvalidProxyRouteError");

const debug = debugFactory("apiGateway:proxyRoute");

/**
 *  @typedef { import("../types").IProxyRouteConfig } IProxyRouteConfig
 *  @typedef { import("../types").IProxyRoute } IProxyRoute
 */

const defaultProxyRoute = {
  method: "GET",
  excludeHeaders: ["Cookie"],
};

/**
 *  given an api endpoint, determines what the proxy route should look like.
 *  this is fully deterministic, so both the server and client can run it and
 *  get the same results. server runs it to mount the proxy in express, client
 *  runs it to know which proxy to call.
 *
 *  e.g.
 *    http://10.1.14.187:4202/pal/cre/v1/users/current_user
 *
 *     for an app mounted on /commercial/listing/ becomes
 *
 *     /commercial/listing/cu/api/h2d695/pal/cre/v1/users/current_user
 *
 * @param { string | IProxyRouteConfig } definition
 * @return { IProxyRoute }
 *
 */
function createProxyRoute(definition) {
  let endpointUrl = "";
  let base = {};
  let useCaToken = false;

  if (typeof definition === "string") {
    endpointUrl = definition;
  } else {
    endpointUrl = definition.endpointUrl;
    base = definition;
  }

  if (definition && definition.useCaToken === true) {
    useCaToken = definition.useCaToken;
  }

  // Fail fast for invalid proxy routes
  if (!usesProxy(endpointUrl)) {
    throw new InvalidProxyRouteError(endpointUrl);
  }

  let url;

  try {
    url = new URL(endpointUrl);
  } catch (e) {
    throw new InvalidProxyRouteError(endpointUrl);
  }

  // the proxy url that will proxy request into the original endpointUrl
  const proxyPathname = calcGatewayPathname(url);

  // pathToRegExp metadata
  const proxyPathKeys = [];
  // this allows for precise matching even endpointUrls that have url rest params
  const proxyPathRegexp = pathToRegexp(proxyPathname, proxyPathKeys);

  const fromParamsToEndpointPath = compile(url.pathname, {
    encode: encodeURIComponent,
  });
  const fromProxyPathToParams = match(proxyPathname, {
    decode: decodeURIComponent,
  });

  return {
    ...defaultProxyRoute,
    ...base,

    // the underlying target endpoint that we are going to proxy
    endpoint: {
      href: url.href,
      origin: url.origin,
      pathname: url.pathname,
      search: url.search,
      // calculate a real url from url rest params (i.e. {listingId: 123} => /123)
      fromParamsToPath: (params) => fromParamsToEndpointPath(params),
    },

    // the proxy endpoint that will eventually forward http calls to the endpoint
    proxy: {
      // the entire url of the proxy endpoint
      pathname: proxyPathname,
      // get the rest params from a url (i.e. /:listingId and /123 => {listingId: 123}
      fromPathToParams: (url) => fromProxyPathToParams(url).params || {},

      regexp: proxyPathRegexp,
      regexpKeys: proxyPathKeys,
    },
    useCaToken: useCaToken,
  };
}

/**
 * @param {Array<IProxyRoute>} proxyRoute
 * @param {string} urlString
 * @return {IEndpointRendered | undefined}
 */
function findMatch(proxyRoutes, urlString, method = "GET") {
  // why? to satisfy the constraints of the URL class
  const fakeOrigin = "http://localhost:1234";
  const url = new URL(urlString, fakeOrigin);
  return proxyRoutes.find((route) => {
    const isSamePathname = route.proxy.regexp.test(url.pathname);
    const isSameMethod = route.method === method;

    return isSamePathname && isSameMethod;
  });
}

/**
 * @param {Array<IProxyRoute>} proxyRoute
 * @param {string} urlString
 * @return {string}
 */
function mapProxyToEndpointPathname(proxyRoute, urlString) {
  // why? to satisfy the constraints of the URL class
  const fakeOrigin = "http://localhost:1234";
  const url = new URL(urlString, fakeOrigin);

  const params = proxyRoute.proxy.fromPathToParams(url.pathname);
  const endpointPath = proxyRoute.endpoint.fromParamsToPath(params);

  debug("mapProxyToEndpointPathname from %s to %s", url.pathname, endpointPath);

  return `${endpointPath}${url.search}`;
}

/**
 * @param {Request} req
 * @param {Array<IProxyRoute>} proxyRoute
 * @return {boolean}
 */
function isAuthorized(req, proxyRoute) {
  if (!proxyRoute.authorityRequired) {
    return true;
  }

  // libAuth session (js-lib-auth aka Authorization library)
  const session = req.tenxSession;
  if (!session) {
    return false;
  }

  return session.hasAuthority([proxyRoute.authorityRequired]);
}

/**
 * Use the proxyRoute exclude headers attribute
 * to cleanup the headers object.
 *
 * @param {Array<IProxyRoute>} proxyRoute
 * @param {object} headers
 * @return {object}
 */
function calcReqHeaders(proxyRoute, headers) {
  return proxyRoute.excludeHeaders.reduce((headers, excludeHeader) => {
    // express stores headers internally in lowercase, so we almost make sure to lowercase
    // the header before trying to delete
    delete headers[excludeHeader.toLowerCase()];
    return headers;
  }, headers);
}

/*
  determines when the client should try to call the proxy instead of use fetch directly
*/
function usesProxy(route) {
  return !!(
    // localhost with a port
    (
      route.match(/http:\/\/localhost:\d{2,4}/) ||
      // IPs with 4 digit port
      route.match(/http:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}:\d{2,4}/) ||
      // ten-x and local URLs
      route.match(/http:\/\/.*\.local:\d{4}/) ||
      route.match(/http:\/\/.*\.tenx_overlay:\d{4}/) ||
      route.match(/http:\/\/.*\.ten-x\.cloud(:\d{4})?/) ||
      route.match(/http:\/\/.*\.ten-x.io:\d{4}/) ||
      // pardot forms
      route.match(/https:\/\/go\.ten-x.com\/l\//) ||
      // Old Configuration server
      route.match(/http:\/\/paas-config-server:\d{4}/) ||
      // New Configuration server
      route.match(
        /http(s?):\/\/service-use1.(dvm|tsm|tsr|prd).tenx.aws.dshrp.com/
      ) ||
      // Token client for POF
      route.match(
        /http(s?):\/\/tokenclient-contact.(dvm|tsm|tsr|prd).dataservices.aws.dshrp.com/
      ) ||
      // we api
      route.match(
        /http(s?):\/\/we-(dvm|tsm|tsr|prd).costar.com/
      ) ||
      route.match(
        /http(s?):\/\/we.costar.com/
      ) ||
      // Contract and Closing proxy
      route.match(
        /http(s?):\/\/contract-closing-proxy-(dvm|tsm|tsr|prd).costar.com/
      ) ||
      route.match(
        /http(s?):\/\/api-use1.(dvm|tsm|tsr|prd).tenx.aws.dshrp.com/
      ) ||
      // Kubernetes address for pods (e.g. http://uaa:9000, http://cre-ms-dms/something, http://cre-ms-dms)
      route.match(/http(s?):\/\/[\w-]+:\d{2,4}/) ||
      // Note that `/` is expected or the end of the string, or `http://google.com/something` would match with `http://google`
      route.match(/http:\/\/[\w-]+(?:\/|$)/)
    )
  );
}

module.exports = {
  calcReqHeaders,
  createProxyRoute,
  findMatch,
  isAuthorized,
  mapProxyToEndpointPathname,
  usesProxy,
};
